import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { isForbidden, organisationErrorMessage } from '../../../../organisations/lib/errorMessage';
import SectionState from './SectionState';
import { dayMonthTime } from './lib/activityFormat';

const senderName = (sender) => (sender === 'user' ? 'Student' : 'PathBuddy');

/**
 * One conversation, fetched the first time it is opened and kept in `cache`
 * for this profile. Text is rendered as text. The box opens at the first
 * message; Jump to latest moves it to the bottom when the thread overflows.
 */
const ChatTranscript = ({ studentId, conversationId, cache }) => {
  const box = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const cached = cache.get(conversationId);
  const [state, setState] = useState(cached || { status: 'loading', data: null, error: '' });
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const hit = attempt === 0 ? cache.get(conversationId) : null;
    if (hit && hit.status !== 'loading') {
      setState(hit);
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', data: null, error: '' });
    organisationsAPI.getStudentChatTranscript(studentId, conversationId)
      .then((data) => {
        const next = {
          status: data.messages && data.messages.length ? 'ready' : 'empty',
          data,
          error: '',
        };
        cache.set(conversationId, next);
        if (!cancelled) setState(next);
      })
      .catch((err) => {
        const next = isForbidden(err)
          ? { status: 'forbidden', data: null, error: '' }
          : { status: 'error', data: null, error: organisationErrorMessage(err, 'Please try again.') };
        if (!cancelled) setState(next);
      });
    return () => { cancelled = true; };
  }, [studentId, conversationId, cache, attempt]);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) {
      setOverflows(false);
      return;
    }
    el.scrollTop = 0;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [state]);

  const jump = () => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const view = {
    ...state,
    retry: () => {
      cache.delete(conversationId);
      setAttempt((n) => n + 1);
    },
  };
  const messages = state.status === 'ready' && state.data ? state.data.messages : [];

  return (
    <div className="td-act-threadwrap">
      <SectionState
        state={view}
        what="the transcript"
        emptyTitle="No messages in this conversation."
      />
      {messages.length ? (
        <>
          <div
            className="td-act-thread"
            ref={box}
            role="log"
            aria-label="Transcript"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`td-act-bubble td-act-bubble--${message.sender === 'user' ? 'student' : 'pathbuddy'}`}
              >
                <div className="td-act-bubble__who">
                  {`${senderName(message.sender)} · ${dayMonthTime(message.at)}`}
                </div>
                <div className="td-act-bubble__text">{message.text}</div>
              </div>
            ))}
          </div>
          {overflows ? (
            <button type="button" className="td-btn td-btn--small td-act-jump" onClick={jump}>
              Jump to latest ↓
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ChatTranscript;
